import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Sparkles, Camera, FileText, PhoneCall, Loader2,
  DollarSign, MapPin, Home, ExternalLink, Mail, MessageSquare,
  Wallet, TrendingUp, Save, UserPlus, Layers, RefreshCw, Send, Download,
} from "lucide-react";
import SendToPipelineDialog from "@/components/SendToPipelineDialog";
import EmailComposeDialog from "@/components/EmailComposeDialog";
import PropertyPhotos from "@/components/PropertyPhotos";
import CallSmsDialog from "@/components/CallSmsDialog";
import SkipPasteDialog from "@/components/SkipPasteDialog";
import PropertyMediaGallery from "@/components/PropertyMediaGallery";
import SmartOfferRecommender from "@/components/SmartOfferRecommender";
import { exportCsv } from "@/lib/csv";

const REPAIR_TIERS = [
  { value: "cosmetic", label: "🟢 Cosmetic Makeover · $20/sqft" },
  { value: "standard", label: "🟡 Standard Flip · $35/sqft" },
  { value: "heavy",    label: "🟠 Heavy Rehab · $60/sqft" },
  { value: "full_gut", label: "🔴 Full Gut · $100/sqft" },
  { value: "itemized", label: "🔧 Itemized · check what needs work" },
  { value: "custom",   label: "Custom amount" },
];

// Median US flip-market pricing per line item — user can edit any value.
const ITEMIZED_DEFAULTS = [
  { key: "roof",         label: "Roof",         cost: 12000 },
  { key: "hvac",         label: "HVAC",         cost: 8000  },
  { key: "plumbing",     label: "Plumbing",     cost: 6000  },
  { key: "electrical",   label: "Electrical",   cost: 8000  },
  { key: "kitchen",      label: "Kitchen",      cost: 20000 },
  { key: "bathrooms",    label: "Bathrooms",    cost: 10000 },
  { key: "windows",      label: "Windows",      cost: 8000  },
  { key: "flooring",     label: "Flooring",     cost: 8000  },
  { key: "drywall",      label: "Drywall",      cost: 4000  },
  { key: "foundation",   label: "Foundation",   cost: 12000 },
  { key: "pool",         label: "Pool",         cost: 6000  },
  { key: "landscaping",  label: "Landscaping",  cost: 3000  },
];

function fmtField(k, v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number" && k.match(/(price|value|balance|equity|arv|taxes|hoa|rental|sale)/i))
    return money(v);
  return String(v);
}

const FIELD_LABELS = {
  owner_name: "Owner",
  mailing_address: "Mailing Address",
  property_value: "Property Value",
  arv: "ARV",
  mortgage_balance: "Mortgage Balance (est.)",
  payoff_amount: "Payoff Amount (confirmed)",
  loan_type: "Loan Type",
  interest_rate: "Interest Rate",
  equity: "Equity",
  property_taxes: "Property Taxes",
  hoa: "HOA",
  lot_size: "Lot (acres)",
  sqft: "Sqft",
  bedrooms: "Beds",
  bathrooms: "Baths",
  year_built: "Year Built",
  flood_zone: "Flood Zone",
  rental_estimate: "Rent Est.",
  days_owned: "Days Owned",
  last_sale_price: "Last Sale",
};

const FLAGS = [
  ["vacant", "Vacant"],
  ["tax_delinquent", "Tax Delinquent"],
  ["out_of_state_owner", "Out of State Owner"],
  ["probate", "Probate"],
  ["code_violations", "Code Violations"],
  ["foreclosure", "Foreclosure"],
  ["bankruptcy", "Bankruptcy"],
  ["liens", "Liens"],
];

export default function PropertyLookup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [property, setProperty] = useState(null); // enriched + local edits
  const [savedId, setSavedId] = useState(null);
  const [zillowPhoto, setZillowPhoto] = useState(null); // {photo_url, zpid, matched_address}

  // Auto-fetch Zillow front-of-house photo whenever a property loads.
  // Cached 30 days on the backend so repeat lookups are free. Silent on
  // error — the media gallery falls back to the aerial view.
  useEffect(() => {
    setZillowPhoto(null);
    const addr = property?.address;
    if (!addr) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/property/mls-photo?address=${encodeURIComponent(addr)}`
        );
        if (!cancelled && data?.photo_url) setZillowPhoto(data);
      } catch (_) { /* silent — no photo available */ }
    })();
    return () => { cancelled = true; };
  }, [property?.address]);
  // ---- Address autocomplete state ----
  const [acResults, setAcResults] = useState([]);
  const [acOpen, setAcOpen] = useState(false);
  const [acIdx, setAcIdx] = useState(-1);
  const acAbortRef = useRef(null);
  const acDebounceRef = useRef(null);
  const acJustPickedRef = useRef(false);

  // Load a saved property when navigated with ?id=<property_id>
  useEffect(() => {
    const pid = searchParams.get("id");
    if (!pid) return;
    setBusy(true);
    api.get(`/properties/${pid}`)
      .then(({ data }) => {
        setProperty({ ...data, cached: true, source: data.source || "user_saved" });
        setSavedId(data.id);
        setAddress(data.address || "");
      })
      .catch(() => toast.error("Saved property not found"))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("id")]);

  // Deep-link with ?address=<addr> — prefills and auto-runs Analyze so the
  // user lands directly on the tier calculator. Used by the "Analyze in
  // Property Lookup" button on the Lead detail drawer.
  const autoAnalyzedRef = useRef(false);
  useEffect(() => {
    const qaddr = (searchParams.get("address") || "").trim();
    if (!qaddr || autoAnalyzedRef.current) return;
    autoAnalyzedRef.current = true;
    setAddress(qaddr);
    // fire after next tick so state settles + the shared `analyze()` handler
    // (defined below) is in scope on the next render
    setTimeout(() => {
      try { analyze(null, qaddr); } catch (_) { /* handler not yet defined */ }
      // clear the query so a refresh doesn't re-trigger
      setSearchParams({});
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("address")]);

  // ---- Address autocomplete: debounced fetch as user types ----
  useEffect(() => {
    // Skip the fetch that would fire right after picking a suggestion.
    if (acJustPickedRef.current) {
      acJustPickedRef.current = false;
      return;
    }
    const q = (address || "").trim();
    if (q.length < 3) {
      setAcResults([]); setAcOpen(false); setAcIdx(-1);
      return;
    }
    if (acDebounceRef.current) clearTimeout(acDebounceRef.current);
    acDebounceRef.current = setTimeout(async () => {
      if (acAbortRef.current) acAbortRef.current.abort();
      const ctrl = new AbortController();
      acAbortRef.current = ctrl;
      try {
        // POST to REAPI-backed /properties/autocomplete (2026-02-11: was
        // GET /property/autocomplete against a legacy Zillow scraper —
        // now points at REAPI /v2/AutoComplete via our shim endpoint).
        const { data } = await api.post("/properties/autocomplete",
          { search: q }, { signal: ctrl.signal });
        setAcResults(data.results || []);
        setAcOpen((data.results || []).length > 0);
        setAcIdx(-1);
      } catch (_) { /* aborted or offline — silent */ }
    }, 220);
    return () => acDebounceRef.current && clearTimeout(acDebounceRef.current);
  }, [address]);

  const pickSuggestion = (s) => {
    acJustPickedRef.current = true;
    setAddress(s.address);
    setAcOpen(false); setAcResults([]); setAcIdx(-1);
    // Fire analyze immediately with the picked address to avoid the React
    // state-update lag that was causing the wrong property to be fetched.
    analyze(null, s.address);
  };

  // Calculator state — seeded from user's Settings > Contract Terms so
  // any change to Settings.default_wholesale_fee / default_investor_profit /
  // default_closing_costs propagates here on next mount. Also mirrored into
  // the offer PDF + contract letter server-side (pdf_service.py + server.py
  // context builder) so the same values flow all the way to the signed
  // contract. Fetched lazily below.
  const [dealMode, setDealMode] = useState("off_market_standard"); // "mls" | "off_market_standard" | "off_market_appealing"
  const [tier, setTier] = useState("standard");
  const [customRepair, setCustomRepair] = useState(40000);
  const [investorProfit, setInvestorProfit] = useState(40000);
  const [wholesaleFee, setWholesaleFee] = useState(25000);
  const [closingCosts, setClosingCosts] = useState(15000);
  const [holdingCosts, setHoldingCosts] = useState(3000);
  const [realtorPct, setRealtorPct] = useState(6); // percent of ARV
  const [targetPct, setTargetPct] = useState(70);  // MLS target margin
  const [calc, setCalc] = useState(null);

  // Sync defaults FROM Settings > Contract Terms once on mount. This
  // guarantees Settings → Calculator → Contract PDF all use the exact
  // same numbers: profile.default_wholesale_fee, default_investor_profit,
  // default_closing_costs. Only overwrite the local state if the user
  // has actually set a non-null value on their profile — otherwise we
  // keep the sensible seed defaults above.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile");
        if (data?.default_wholesale_fee !== undefined && data.default_wholesale_fee !== null)
          setWholesaleFee(Number(data.default_wholesale_fee) || 0);
        if (data?.default_investor_profit !== undefined && data.default_investor_profit !== null)
          setInvestorProfit(Number(data.default_investor_profit) || 0);
        if (data?.default_closing_costs !== undefined && data.default_closing_costs !== null)
          setClosingCosts(Number(data.default_closing_costs) || 0);
      } catch (_) { /* fall back to seed defaults */ }
    })();
  }, []);

  // Itemized rehab estimator — each entry: { checked, cost }
  const [itemized, setItemized] = useState(() =>
    ITEMIZED_DEFAULTS.reduce(
      (acc, it) => ({ ...acc, [it.key]: { checked: false, cost: it.cost } }),
      {},
    ),
  );
  const itemizedTotal = ITEMIZED_DEFAULTS.reduce(
    (sum, it) =>
      sum + (itemized[it.key]?.checked ? Number(itemized[it.key]?.cost || 0) : 0),
    0,
  );

  // AI states
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  // Repair estimator
  const [repairEst, setRepairEst] = useState(null);
  const [repairBusy, setRepairBusy] = useState(false);
  const fileInput = useRef();

  // Skip trace
  const [skip, setSkip] = useState(null);
  const [skipBusy, setSkipBusy] = useState(false);

  // On-demand RentCast fetches (each = 1 credit)
  const [listingBusy, setListingBusy] = useState(false);
  const [rentBusy, setRentBusy] = useState(false);
  const [compsBusy, setCompsBusy] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [callSms, setCallSms] = useState(null); // { phone, tab }
  const [skipPasteOpen, setSkipPasteOpen] = useState(false);
  const [latestOfferId, setLatestOfferId] = useState(null);
  const [comps, setComps] = useState(null);
  const [market, setMarket] = useState(null);
  const [marketBusy, setMarketBusy] = useState(false);

  // Auto-load ZIP market stats whenever a new property is loaded. Cached
  // 7 days on the backend so re-lookups don't burn credits.
  useEffect(() => {
    const zip = property?.zip || property?.zipCode;
    if (!zip) { setMarket(null); return; }
    let cancelled = false;
    (async () => {
      setMarketBusy(true);
      try {
        const { data } = await api.post("/properties/enrich/market", { zip });
        if (!cancelled) setMarket(data);
      } catch (_) { /* silent */ }
      finally { if (!cancelled) setMarketBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [property?.zip, property?.zipCode]);

  const analyze = async (e, overrideAddress, { force = false } = {}) => {
    e?.preventDefault?.();
    // Use the explicit override when available (autocomplete pick) instead of
    // reading `address` from state, which may not have flushed the update yet
    // and would send RentCast the partial-typed string → wrong property.
    const addr = (overrideAddress ?? address).trim();
    if (!addr) return;
    setBusy(true);
    setSavedId(null);
    setAiAnalysis(null);
    setRepairEst(null);
    setSkip(null);
    setCalc(null);
    setComps(null);
    // Drop any ?id= so a fresh search doesn't stay tied to an old saved id
    if (searchParams.get("id")) setSearchParams({});
    try {
      const { data } = await api.post("/properties/enrich", { address: addr, force });
      setProperty(data);
      if (data.cached && !force) {
        toast.success(
          data.source === "user_saved"
            ? "Loaded from your saved properties"
            : "Loaded from cache — no API call used",
        );
      } else if (force) {
        toast.success("Fresh data fetched — cache updated");
      } else {
        toast.success("Property enriched");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrichment failed");
    } finally {
      setBusy(false);
    }
  };

  const refreshFromSource = () => {
    const addr = property?.address || address;
    if (!addr) return;
    analyze(null, addr, { force: true });
  };

  const saveProperty = async () => {
    if (!property) return;
    try {
      let saved;
      if (savedId) {
        const { data } = await api.patch(`/properties/${savedId}`, property);
        saved = data;
      } else {
        const { data } = await api.post("/properties", property);
        saved = data;
        setSavedId(data.id);
      }
      setProperty(saved);
      toast.success("Saved to your properties");
    } catch (err) {
      toast.error("Save failed");
    }
  };

  // Create (or find) a lead attached to this property, then optionally
  // open the "Send to pipeline" dialog so the user can drop it straight
  // into Wholesale / Acquisitions / Flips.
  const [addLeadBusy, setAddLeadBusy] = useState(false);
  const [createdLeadId, setCreatedLeadId] = useState(null);
  const [sendPipelineOpen, setSendPipelineOpen] = useState(false);
  // Smart Offer tiers (populated once the recommender computes them) + the
  // exact dollar amount that will attach to the lead when Add-to-Lead fires.
  const [smartTiers, setSmartTiers] = useState([]);       // [{key, label, price, arv_pct}, ...]
  const [pickedTierKey, setPickedTierKey] = useState(null);
  const [pickedOfferAmount, setPickedOfferAmount] = useState("");
  const addToLead = async ({ openPipeline = false } = {}) => {
    if (!property) return;
    setAddLeadBusy(true);
    try {
      // Save the property first if we haven't already
      let propId = savedId;
      if (!propId) {
        const { data: p } = await api.post("/properties", property);
        setSavedId(p.id);
        propId = p.id;
      }
      // Choose the offer amount: prefer the user-picked amount from the
      // tier chips / custom input, fall back to the property's computed MAO.
      const chosenOffer = pickedOfferAmount === "" || pickedOfferAmount == null
        ? (property.mao ?? null)
        : Number(pickedOfferAmount) || null;
      const { data } = await api.post("/leads", {
        name: property.owner_name || "Property Owner",
        phone: null,
        email: null,
        stage: "new_lead",
        motivation_score: 50,
        property_address: property.address,
        property_arv: property.arv ?? null,
        property_mao: property.mao ?? null,
        offer_amount: chosenOffer,
        payoff_amount: property.mortgage_balance ?? null,
        notes: `Added from Property Lookup — ${property.address}${
          pickedTierKey ? ` · Offer tier: ${pickedTierKey}` : ""
        }`,
        next_action: "First contact",
        source_tag: "property_lookup",
        property_id: propId,
      });
      setCreatedLeadId(data.id);
      toast.success(`Added ${data.name} as a new lead`);
      if (openPipeline) setSendPipelineOpen(true);
      return data.id;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add-to-lead failed");
    } finally { setAddLeadBusy(false); }
  };

  const runCalculation = async () => {
    if (!property?.arv || !property?.sqft) {
      toast.error("Need ARV and Sqft first");
      return;
    }
    let customValue = null;
    if (tier === "custom") customValue = Number(customRepair);
    else if (tier === "itemized") customValue = Number(itemizedTotal);
    const { data } = await api.post("/deals/calculate", {
      arv: Number(property.arv),
      sqft: Number(property.sqft),
      repair_tier: tier,
      custom_repair: customValue,
      investor_profit: Number(investorProfit),
      wholesale_fee: Number(wholesaleFee),
      closing_costs: Number(closingCosts),
      mode: dealMode,
      target_pct: Number(targetPct) / 100,
      holding_costs: Number(holdingCosts),
      realtor_pct: Number(realtorPct) / 100,
    });
    setCalc(data);
  };

  const runAiAnalysis = async () => {
    setAiBusy(true);
    try {
      // Old behavior asked "run the calculator first". User wants a
      // one-click "Run Comp Analysis" that does everything itself:
      //   1. Kick the calculator if the rep hasn't manually run it yet
      //      (uses the property's ARV + sqft + current settings-seeded
      //      wholesale/investor/closing defaults). Safe to no-op if
      //      ARV/sqft are missing — the AI analyzer works from the
      //      property doc alone.
      //   2. Pull live comps for THIS address so the analysis reflects
      //      actual nearby sales (accuracy demand from the user).
      //   3. Persist the property (if it wasn't saved yet) so the AI
      //      endpoint has a stable property_id to attach the analysis to.
      //   4. Fire the AI analyzer with both the comps + latest calc.
      let currentCalc = calc;
      if (!currentCalc && property?.arv && property?.sqft) {
        try {
          const { data } = await api.post("/deals/calculate", {
            arv: Number(property.arv),
            sqft: Number(property.sqft),
            repair_tier: tier,
            custom_repair: tier === "custom" ? Number(customRepair)
                         : tier === "itemized" ? Number(itemizedTotal)
                         : null,
            investor_profit: Number(investorProfit),
            wholesale_fee:   Number(wholesaleFee),
            closing_costs:   Number(closingCosts),
            mode: dealMode,
            target_pct: Number(targetPct) / 100,
            holding_costs: Number(holdingCosts),
            realtor_pct:   Number(realtorPct) / 100,
          });
          currentCalc = data;
          setCalc(data);
        } catch (_) { /* non-fatal, AI can still run without calc */ }
      }
      // Fetch comps if we don't already have them — this is the
      // "accurate comp" the user asked for.
      if (!comps) {
        try { await fetchComps(); } catch (_) { /* non-fatal */ }
      }
      // Persist the property if new — AI endpoint needs a stable id.
      let pid = savedId;
      if (!pid) {
        const { data } = await api.post("/properties", property);
        pid = data.id;
        setSavedId(data.id);
        setProperty(data);
      }
      // Fire the AI analyzer with whatever calc + comps we have.
      const { data: aiData } = await api.post("/deals/ai-analyze", {
        property_id: pid,
        deal_calc:   currentCalc || {},
      });
      setAiAnalysis(aiData);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comp analysis failed");
    } finally {
      setAiBusy(false);
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setRepairBusy(true);
    try {
      const b64s = await Promise.all(
        files.slice(0, 6).map(
          (f) =>
            new Promise((resolve) => {
              const r = new FileReader();
              r.onload = () => {
                const s = String(r.result);
                resolve(s.substring(s.indexOf(",") + 1));
              };
              r.readAsDataURL(f);
            }),
        ),
      );
      const { data } = await api.post("/deals/repair-estimate", {
        sqft: Number(property?.sqft || 1500),
        images_base64: b64s,
      });
      setRepairEst(data);
      toast.success("Repair estimate ready");
    } catch (err) {
      toast.error("Repair estimator failed");
    } finally {
      setRepairBusy(false);
    }
  };

  // Per-offer overrides dialog
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrides, setOverrides] = useState({
    override_inspection_days: "",
    override_closing_days: "",
    override_closing_costs_paid_by: "",
    override_use_or_assigns: null,
    override_state: "",
    earnest_money: "",
  });

  const generateOfferWithTerms = async (customOverrides) => {
    if (!calc || !savedId) {
      toast.error("Save property + calc first");
      return;
    }
    const price = aiAnalysis?.recommended_offer || calc.mao;
    const payload = {
      property_id: savedId,
      purchase_price: Math.max(0, Math.round(price)),
    };
    if (customOverrides) {
      if (customOverrides.earnest_money !== "")
        payload.earnest_money = Number(customOverrides.earnest_money);
      if (customOverrides.override_inspection_days !== "")
        payload.override_inspection_days = Number(customOverrides.override_inspection_days);
      if (customOverrides.override_closing_days !== "")
        payload.override_closing_days = Number(customOverrides.override_closing_days);
      if (customOverrides.override_closing_costs_paid_by)
        payload.override_closing_costs_paid_by = customOverrides.override_closing_costs_paid_by;
      if (customOverrides.override_use_or_assigns !== null)
        payload.override_use_or_assigns = customOverrides.override_use_or_assigns;
      if (customOverrides.override_state)
        payload.override_state = customOverrides.override_state;
    }
    try {
      const { data } = await api.post("/offers", payload);
      setLatestOfferId(data.id);
      toast.success("Offer created");
      const pdf = await api.get(`/offers/${data.id}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([pdf.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `offer_${data.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      setShowOverrides(false);
    } catch (err) {
      toast.error("Offer generation failed");
    }
  };

  const generateOffer = () => generateOfferWithTerms(null);
  const generateOfferWithOverrides = () => generateOfferWithTerms(overrides);

  const runSkipTrace = async () => {
    if (!property) return;
    // Save the property first if it's not persisted yet (skip-trace needs a
    // stable property_id). We use the freshly-saved ID directly instead of
    // reading `savedId`/`property.id` which are still stale in this closure.
    let propId = savedId || property.id;
    try {
      if (!propId) {
        const { data } = await api.post("/properties", property);
        propId = data.id;
        setSavedId(data.id);
        setProperty(data);
      }
    } catch (_) {
      toast.error("Could not save property before skip trace");
      return;
    }
    if (!propId) {
      toast.error("Missing property id — try re-analyzing the address");
      return;
    }
    setSkipBusy(true);
    try {
      const { data } = await api.post("/skip-trace", { property_id: propId });
      setSkip(data);
      const phoneCount = (data.phones || []).length;
      const emailCount = (data.emails || []).length;
      if (phoneCount || emailCount) {
        toast.success(`Found ${phoneCount} phone(s) · ${emailCount} email(s)`);
      } else {
        toast.info("No contacts found for this owner");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Skip trace failed");
    } finally {
      setSkipBusy(false);
    }
  };

  const fetchListing = async () => {
    if (!property?.address) return;
    setListingBusy(true);
    try {
      const { data } = await api.post("/properties/enrich/listing", {
        address: property.address,
      });
      setProperty((p) => ({
        ...p,
        list_price: data.list_price,
        list_status: data.list_status,
        days_on_market: data.days_on_market,
        listed_date: data.listed_date,
      }));
      if (data.list_price) {
        toast.success(
          data._cached
            ? `Cached: listed ${money(data.list_price)}`
            : `Listed at ${money(data.list_price)} (1 credit)`,
        );
      } else {
        toast.info(data._cached ? "No active listing (cached)" : "No active listing found");
      }
      loadQuota();
    } catch (err) {
      toast.error("Failed to fetch listing");
    } finally {
      setListingBusy(false);
    }
  };

  const fetchRent = async () => {
    if (!property?.address) return;
    setRentBusy(true);
    try {
      const { data } = await api.post("/properties/enrich/rent", {
        address: property.address,
      });
      setProperty((p) => ({
        ...p,
        rental_estimate: data.rental_estimate,
        rental_range_low: data.rental_range_low,
        rental_range_high: data.rental_range_high,
      }));
      if (data.rental_estimate) {
        toast.success(
          data._cached
            ? `Cached rent: ${money(data.rental_estimate)}/mo`
            : `Rent estimate: ${money(data.rental_estimate)}/mo (1 credit)`,
        );
      } else {
        toast.info("No rent estimate available");
      }
      loadQuota();
    } catch (err) {
      toast.error("Failed to fetch rent estimate");
    } finally {
      setRentBusy(false);
    }
  };

  const fetchComps = async () => {
    if (!property?.address) return;
    setCompsBusy(true);
    try {
      const { data } = await api.post("/properties/enrich/comps", {
        address: property.address,
        sqft: property.sqft,
      });
      setComps(data);
      if (data.comps_suggested_arv) {
        toast.success(
          data._cached
            ? `${data.comps_count} comps loaded from cache`
            : `${data.comps_count} sold comps · suggested ARV ${money(data.comps_suggested_arv)} (1 credit)`,
        );
      } else {
        toast.info("No comparable sales found");
      }
      loadQuota();
    } catch (err) {
      toast.error("Failed to fetch comps");
    } finally {
      setCompsBusy(false);
    }
  };

  const applySuggestedArv = () => {
    if (!comps?.comps_suggested_arv) return;
    setProperty((p) => ({ ...p, arv: comps.comps_suggested_arv }));
    toast.success(`ARV set to ${money(comps.comps_suggested_arv)}`);
  };

  // Quota widget
  const [quota, setQuota] = useState(null);
  const loadQuota = async () => {
    try {
      const { data } = await api.get("/rentcast/quota");
      setQuota(data);
    } catch (_) { /* silent */ }
  };
  useEffect(() => { loadQuota(); }, []);

  // motivation donut chart data
  const scoreData = property
    ? [
        { name: "score", value: property.motivation_score || 0 },
        { name: "rest", value: 100 - (property.motivation_score || 0) },
      ]
    : null;

  return (
    <div className="p-6 md:p-10 max-w-[1400px]" data-testid="property-lookup-page">
      <div className="reveal">
        <span className="app-eyebrow">/ Instant Offer Engine</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3">
          Property Lookup
        </h1>
        <p className="text-[var(--app-text-muted)] mt-3 max-w-2xl leading-relaxed">
          Enter any US property address. We pull ownership, financials, and
          motivation signals, then run the calculator and AI analysis to
          recommend the maximum cash offer.
        </p>
      </div>

      <form onSubmit={analyze} className="mt-8 flex gap-3 reveal reveal-2 relative z-[700]">
        <div className="flex-1 relative">
          <MapPin
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--app-text-muted)]"
          />
          <Input
            data-testid="property-search-input"
            className="pl-11 h-12 rounded-full text-base border-[var(--app-line)] focus-visible:ring-[var(--app-accent)]/50"
            placeholder="123 Main St, Phoenix, AZ 85001"
            value={address}
            autoComplete="off"
            onChange={(e) => setAddress(e.target.value)}
            onFocus={() => acResults.length > 0 && setAcOpen(true)}
            onBlur={() => setTimeout(() => setAcOpen(false), 150)}
            onKeyDown={(e) => {
              if (!acOpen || acResults.length === 0) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setAcIdx((i) => Math.min(i + 1, acResults.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setAcIdx((i) => Math.max(i - 1, -1));
              } else if (e.key === "Enter" && acIdx >= 0) {
                e.preventDefault();
                pickSuggestion(acResults[acIdx]);
              } else if (e.key === "Escape") {
                setAcOpen(false);
              }
            }}
          />
          {acOpen && acResults.length > 0 && (
            <div
              className="absolute z-[600] mt-1.5 left-0 right-0 bg-white border border-[var(--app-line)] rounded-2xl shadow-lg overflow-hidden"
              data-testid="property-autocomplete-list"
            >
              {acResults.map((s, i) => (
                <button
                  type="button"
                  key={`${s.id || s.address}-${i}`}
                  data-testid={`property-autocomplete-item-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                  onMouseEnter={() => setAcIdx(i)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors border-b border-[var(--app-line)] last:border-b-0 ${
                    i === acIdx
                      ? "bg-[var(--app-accent)]/10"
                      : "hover:bg-[var(--app-surface)]"
                  }`}
                >
                  <MapPin size={14} className="mt-0.5 text-[var(--app-accent-dark)] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.address}</div>
                    <div className="text-[11px] text-[var(--app-text-muted)] mt-0.5 flex items-center gap-2 flex-wrap">
                      {s.county && <span>{s.county}</span>}
                      {s.apn && <span className="font-mono opacity-70">APN {s.apn}</span>}
                    </div>
                  </div>
                </button>
              ))}
              <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] bg-[var(--app-surface)]">
                Address suggestions · ↑↓ to navigate · Enter to select
              </div>
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="property-search-submit"
          className="app-btn app-btn-primary h-12 px-6"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />}
          <span>{busy ? "Analyzing…" : "Analyze"}</span>
        </button>
      </form>

      {property && (
        <>
          {/* Property + Score row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            <div className="card-flat lg:col-span-2 overflow-hidden reveal">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="h-56 md:h-72 relative">
                  <PropertyMediaGallery
                    address={property.address}
                    photos={[
                      // Zillow front-of-house photo comes first when we have it
                      ...(zillowPhoto?.photo_url ? [zillowPhoto.photo_url] : []),
                      ...(property.photos || []),
                    ]}
                    height="100%"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                      Property
                    </div>
                    {property.cached && (
                      <span
                        data-testid="cache-badge"
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[var(--app-surface)] text-[var(--app-text-muted)] rounded-full"
                        title="Loaded from cache — no API call used"
                      >
                        {property.source === "user_saved" ? "Saved" : "Cached"}
                      </span>
                    )}
                  </div>
                  <h2 className="font-display text-2xl font-bold mt-1 leading-tight">
                    {property.address}
                  </h2>
                  {property.cached && (
                    <button
                      onClick={refreshFromSource}
                      disabled={busy}
                      data-testid="refresh-from-source-btn"
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                      title="Bypass cache and re-query REAPI + RentCast — costs 1-2 credits but returns the freshest data"
                    >
                      <RefreshCw size={10} className={busy ? "animate-spin" : ""} />
                      Refresh from source
                    </button>
                  )}
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Owner</span>
                      <span className="font-medium">{property.owner_name || "—"}</span>
                    </div>
                    {/* Zillow Zestimate — sourced primarily from the
                        /properties/enrich payload (Zillow scraper is the
                        primary provider now), falling back to the photo
                        endpoint's field for cached rows saved before the
                        Zillow-primary migration. */}
                    {(property.zestimate || zillowPhoto?.zestimate) ? (
                      <div className="flex justify-between" data-testid="field-zestimate">
                        <span className="text-muted-foreground inline-flex items-center gap-1">
                          Zestimate
                          <a
                            href={(property.zpid || zillowPhoto?.zpid) ? `https://www.zillow.com/homedetails/${property.zpid || zillowPhoto?.zpid}_zpid/` : "https://www.zillow.com"}
                            target="_blank" rel="noreferrer"
                            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                            title="Open on Zillow"
                          >
                            Zillow
                          </a>
                        </span>
                        <span className="font-mono font-semibold text-blue-800">
                          {money(property.zestimate || zillowPhoto?.zestimate)}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between" data-testid="field-zestimate-missing">
                        <span className="text-muted-foreground">Zestimate</span>
                        <span className="text-muted-foreground italic text-xs">
                          Not available on Zillow
                        </span>
                      </div>
                    )}
                    {(property.rent_zestimate || zillowPhoto?.rent_zestimate) ? (
                      <div className="flex justify-between" data-testid="field-rent-zestimate">
                        <span className="text-muted-foreground">Rent Zestimate</span>
                        <span className="font-mono text-[13px] text-muted-foreground">
                          {money(property.rent_zestimate || zillowPhoto?.rent_zestimate)}/mo
                        </span>
                      </div>
                    ) : null}
                    {property.list_price && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          List Price
                          {property.list_status && (
                            <span className="ml-1 text-[10px] uppercase tracking-widest text-emerald-700">
                              {property.list_status}
                            </span>
                          )}
                        </span>
                        <span
                          className="font-mono"
                          data-testid="field-list-price"
                        >
                          {money(property.list_price)}
                          {property.days_on_market !== null &&
                            property.days_on_market !== undefined && (
                              <span className="text-xs text-muted-foreground ml-2">
                                {property.days_on_market}d
                              </span>
                            )}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        ARV
                        <span
                          className="ml-1 text-[10px] uppercase tracking-widest text-muted-foreground"
                          title="Estimated post-repair value from RentCast AVM. Override with your own comps below."
                        >
                          · post-repair
                        </span>
                      </span>
                      <span className="font-mono">{money(property.arv)}</span>
                    </div>

                    {/* Value Triangulation — show all 3 sources side-by-side */}
                    {property.arv_sources && (
                      <div className="rounded-xl border border-[var(--app-line)] bg-slate-50 p-3 my-1"
                           data-testid="value-triangulation-panel">
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                          Value triangulation · click one to lock as ARV
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            {
                              key: "comps",
                              label: "Comps",
                              value: property.arv_sources.comps?.value,
                              subtitle: property.arv_sources.comps?.count
                                ? `${property.arv_sources.comps.count} sold`
                                : null,
                              tone: "emerald",
                            },
                            {
                              key: "avm",
                              label: "AVM",
                              value: property.arv_sources.avm?.value,
                              subtitle:
                                property.arv_sources.avm?.confidence === "low"
                                  ? "low conf"
                                  : property.arv_sources.avm?.confidence,
                              tone: "sky",
                            },
                            {
                              key: "tax_assessed",
                              label: "Tax × 1.15",
                              value: property.arv_sources.tax_assessed?.value,
                              subtitle: "assessed",
                              tone: "amber",
                            },
                          ].map((s) => {
                            const isActive = property.arv_source === s.key;
                            const tones = {
                              emerald: isActive ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-emerald-300 text-emerald-900 hover:bg-emerald-50",
                              sky:     isActive ? "bg-sky-600 text-white border-sky-600"         : "bg-white border-sky-300 text-sky-900 hover:bg-sky-50",
                              amber:   isActive ? "bg-amber-600 text-white border-amber-600"    : "bg-white border-amber-300 text-amber-900 hover:bg-amber-50",
                            }[s.tone];
                            return (
                              <button
                                key={s.key}
                                type="button"
                                disabled={!s.value}
                                onClick={() => {
                                  if (!s.value) return;
                                  setProperty((p) => ({ ...p, arv: s.value, arv_source: s.key }));
                                  toast.success(`ARV set to ${money(s.value)} · ${s.label}`);
                                }}
                                data-testid={`arv-source-${s.key}`}
                                className={`rounded-lg border p-2 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tones}`}
                              >
                                <div className="text-[9px] uppercase tracking-widest opacity-80">{s.label}</div>
                                <div className="font-mono text-sm font-bold leading-tight">
                                  {s.value ? money(s.value) : "—"}
                                </div>
                                {s.subtitle && (
                                  <div className={`text-[9px] mt-0.5 ${isActive ? "opacity-90" : "opacity-60"}`}>
                                    {s.subtitle}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        AVM Range
                        {property.avm_low_confidence && (
                          <span
                            className="ml-1 text-[10px] uppercase tracking-widest text-destructive"
                            title="Wide RentCast AVM range — verify with your own comps"
                          >
                            · low conf
                          </span>
                        )}
                      </span>
                      <span
                        className="font-mono text-muted-foreground text-xs text-right"
                        data-testid="field-avm-price"
                      >
                        {property.avm_range_low && property.avm_range_high ? (
                          <>
                            {money(property.avm_range_low)} – {money(property.avm_range_high)}
                          </>
                        ) : (
                          money(property.avm_price || property.property_value)
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Equity
                        {(property.equity == null) && (
                          <span
                            className="ml-1 text-[10px] uppercase tracking-widest text-muted-foreground/70"
                            title="Mortgage balance data unavailable for this address — verify with title search or ask the seller directly"
                          >
                            · unknown
                          </span>
                        )}
                        {property.free_clear && property.equity != null && (
                          <span
                            className="ml-1 text-[10px] uppercase tracking-widest text-emerald-700"
                            title="No open mortgage — property is fully paid off"
                          >
                            · free & clear
                          </span>
                        )}
                      </span>
                      <span
                        className={`font-mono ${
                          property.equity == null
                            ? "text-muted-foreground"
                            : "text-[hsl(var(--success))]"
                        }`}
                        data-testid="field-equity"
                      >
                        {property.equity == null ? "—" : money(property.equity)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Beds / Baths / Sqft</span>
                      <span className="font-mono">
                        {(() => {
                          // Show a clear "not available" per-field marker
                          // instead of a bare "undefined" or "0" —
                          // reps need to know when data is genuinely missing
                          // vs zero. Zillow scraper leaves any field null
                          // that the listing didn't have.
                          const b  = property.bedrooms  != null ? property.bedrooms  : "—";
                          const ba = property.bathrooms != null ? property.bathrooms : "—";
                          const sf = property.sqft      ? property.sqft.toLocaleString() : "—";
                          return `${b}/${ba}/${sf}`;
                        })()}
                      </span>
                    </div>
                    {/* Lot size — new field, sourced from Zillow's lotAreaValue
                        + lotAreaUnit (normalized to sqft on the backend). */}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lot size</span>
                      <span className="font-mono" data-testid="field-lot-size">
                        {property.lot_size
                          ? `${property.lot_size.toLocaleString()} sqft`
                          : <span className="text-muted-foreground italic">Not available</span>}
                      </span>
                    </div>
                    {/* Year built — RentCast enrichment field. */}
                    {property.year_built != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Year built</span>
                        <span className="font-mono" data-testid="field-year-built">
                          {property.year_built}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info banner: AVM disagrees with active listing — expected on
                      distressed flip deals, but flag it so the user notices. */}
                  {property.list_price &&
                    property.avm_price &&
                    (property.avm_vs_list_delta_pct || 0) > 0.25 && (
                      <div
                        className="mt-3 p-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs rounded-xl"
                        data-testid="avm-list-mismatch-warning"
                      >
                        <div className="font-semibold uppercase tracking-widest text-[10px]">
                          Big spread — verify ARV
                        </div>
                        <div className="mt-1">
                          Listed at {money(property.list_price)} but RentCast estimates
                          renovated value ({money(property.avm_price)}).
                          If the property truly needs heavy repairs, that spread can
                          be your profit — but always confirm ARV against recently
                          sold renovated comps in the neighborhood.
                        </div>
                      </div>
                  )}
                  {!property.list_price && property.avm_low_confidence && (
                    <div
                      className="mt-3 p-3 border border-amber-300 bg-amber-50 text-amber-900 text-xs rounded-xl"
                      data-testid="avm-low-confidence-warning"
                    >
                      <div className="font-semibold uppercase tracking-widest text-[10px]">
                        Low-confidence AVM
                      </div>
                      <div className="mt-1">
                        No active listing found and RentCast&apos;s AVM range is wide
                        ({money(property.avm_range_low)}–{money(property.avm_range_high)}).
                        Verify ARV against your own comps before making an offer.
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {FLAGS.filter(([k]) => property[k]).map(([k, l]) => (
                      <Badge
                        key={k}
                        variant="secondary"
                        className="rounded-full bg-red-50 text-red-600 border-red-200"
                        data-testid={`flag-${k}`}
                      >
                        {l}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    onClick={saveProperty}
                    className="mt-5 w-full rounded-full h-10 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                    data-testid="save-property-button"
                  >
                    {savedId ? "Saved — update" : "Save to my properties"}
                  </Button>

                  {/* Sold Comps button removed 2026-02-17 by owner — each
                      click bills $1 (10 REAPI comps). Reps were clicking
                      it as a reflex. Comps are still callable server-side
                      via /properties/enrich/comps for admin use, and the
                      Sold Comps panel below still renders whatever's
                      already been fetched into `comps`. */}
                </div>
              </div>
            </div>

            <div className="card-flat p-5 reveal reveal-2" data-testid="motivation-card">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Seller Motivation
              </div>
              <h3 className="font-display text-xl font-bold mt-1">Score</h3>

              <div className="relative h-44">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={scoreData}
                      innerRadius={55}
                      outerRadius={78}
                      startAngle={90}
                      endAngle={-270}
                      dataKey="value"
                      stroke="none"
                    >
                      <Cell
                        fill={
                          property.motivation_score >= 70
                            ? "#B91C1C"
                            : property.motivation_score >= 40
                              ? "#B45309"
                              : "#0C0A09"
                        }
                      />
                      <Cell fill="#E7E5E4" />
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="kpi-num text-4xl" data-testid="motivation-score-value">
                    {property.motivation_score || 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    of 100
                  </div>
                </div>
              </div>

              <div className="mt-2">
                <div
                  className={`text-sm font-semibold ${
                    property.motivation_score >= 70
                      ? "text-destructive"
                      : property.motivation_score >= 40
                        ? "text-[hsl(var(--warning))]"
                        : "text-muted-foreground"
                  }`}
                >
                  {property.motivation_score >= 70
                    ? "Hot Lead"
                    : property.motivation_score >= 40
                      ? "Warm Lead"
                      : "Cold Lead"}
                </div>
                <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
                  {(property.motivation_breakdown || []).map((b) => (
                    <div key={b.factor} className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{b.factor}</span>
                      <span className="font-mono">+{b.points}</span>
                    </div>
                  ))}
                  {(!property.motivation_breakdown || property.motivation_breakdown.length === 0) && (
                    <div className="text-xs text-muted-foreground">
                      No motivation factors detected.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Property Photos — visible once the property is saved */}
          {savedId && (
            <div className="card-flat mt-6 reveal reveal-3 p-5" data-testid="property-photos-card">
              <PropertyPhotos
                propertyId={savedId}
                photos={property.photos || []}
                onUpdated={async () => {
                  try {
                    const { data } = await api.get(`/properties/${savedId}`);
                    setProperty((p) => ({ ...p, photos: data.photos || [] }));
                  } catch (_) { /* silent */ }
                }}
              />
            </div>
          )}

          {/* Full detail grid */}
          <div className="card-flat mt-6 reveal reveal-3" data-testid="property-details">
            <div className="p-5 border-b border-border flex items-center gap-2">
              <Home size={16} />
              <div className="font-display text-lg font-bold">Full Property Data</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 p-5 text-sm">
              {Object.entries(FIELD_LABELS).map(([k, l]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {l}
                  </div>
                  <div className="font-mono mt-0.5" data-testid={`field-${k}`}>
                    {fmtField(k, property[k])}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* PropGPT panel removed 2026-02-17 by owner. Component still
              defined at bottom of file so it can be re-enabled by
              dropping <PropGptPanel property={property} /> back in when
              OPENAI_API_KEY is provisioned and reps are ready for it. */}

          {/* Sold Comps grid — populated after user clicks "Sold comps" */}
          {comps && comps.comps && comps.comps.length > 0 && (
            <div className="card-flat mt-6 reveal" data-testid="comps-grid">
              <div className="p-5 border-b border-[var(--app-line)] flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Home size={16} />
                    <div className="font-display text-lg font-bold">
                      Sold Comparables
                    </div>
                    <span className="text-xs text-[var(--app-text-muted)]">
                      · {comps.comps_count} homes within ~0.5 mi, sold last 6 months
                    </span>
                  </div>
                  {/* Apple-to-apple match banner */}
                  <div
                    className="mt-2 flex flex-wrap items-center gap-1.5"
                    data-testid="comps-match-banner"
                  >
                    {comps.subject_pool !== undefined && comps.subject_pool !== null && (
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${
                        comps.subject_pool
                          ? "bg-blue-50 text-blue-700"
                          : "bg-[var(--app-surface)] text-[var(--app-text-muted)]"
                      }`}>
                        {comps.subject_pool ? "🏊 subject has pool — pool comps only" : "no pool — dry-lot comps only"}
                      </span>
                    )}
                    {comps.subject_waterfront !== undefined && comps.subject_waterfront !== null && (
                      <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${
                        comps.subject_waterfront
                          ? "bg-cyan-50 text-cyan-700"
                          : "bg-[var(--app-surface)] text-[var(--app-text-muted)]"
                      }`}>
                        {comps.subject_waterfront ? "🌊 waterfront/canal — matched comps only" : "not waterfront — dry-lot comps only"}
                      </span>
                    )}
                    {comps.comps_filter_relaxed && (
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800"
                        title="Not enough matching pool/waterfront comps within radius — showing unfiltered comps. Verify each row before trusting the median."
                      >
                        ⚠️ filter relaxed — verify manually
                      </span>
                    )}
                    {!comps.comps_filter_relaxed && comps.comps_dropped > 0 && (
                      <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                        {comps.comps_dropped} mismatched comp{comps.comps_dropped === 1 ? "" : "s"} filtered out
                      </span>
                    )}
                    {comps.comps_non_sold_dropped > 0 && (
                      <span
                        className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"
                        title="Active + pending listings are excluded — only closed sales are used for ARV."
                      >
                        {comps.comps_non_sold_dropped} active listing{comps.comps_non_sold_dropped === 1 ? "" : "s"} excluded
                      </span>
                    )}
                  </div>
                </div>
                {comps.comps_suggested_arv && (
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--app-text-muted)]">
                      Suggested ARV
                      <span className="ml-1 opacity-60">
                        ({money(comps.comps_median_ppsf)}/sqft × {property.sqft || 0})
                      </span>
                    </div>
                    <div
                      className="kpi-num text-2xl mt-1"
                      data-testid="comps-suggested-arv"
                    >
                      {money(comps.comps_suggested_arv)}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <button
                        onClick={applySuggestedArv}
                        data-testid="apply-suggested-arv"
                        className="text-[10px] uppercase tracking-[0.15em] text-emerald-700 hover:underline"
                      >
                        Use this ARV →
                      </button>
                      <button
                        onClick={() => exportCsv(
                          comps.comps || [],
                          [
                            { key: "address",        label: "Address" },
                            { key: "beds",           label: "Beds" },
                            { key: "baths",          label: "Baths" },
                            { key: "sqft",           label: "Sqft" },
                            { key: "year_built",     label: "Year Built" },
                            { key: "sale_price",     label: "Sale Price" },
                            { key: "sale_date",      label: "Sale Date" },
                            { key: "price_per_sqft", label: "$/Sqft" },
                            { key: "distance",       label: "Distance (mi)" },
                          ],
                          `flippilot-comps-${(property.address || "").replace(/[^a-z0-9]+/gi, "-")}.csv`,
                        )}
                        data-testid="comps-export-csv"
                        className="text-[10px] uppercase tracking-[0.15em] text-slate-600 hover:text-slate-900 hover:underline inline-flex items-center gap-1"
                        title="Download this comps table as CSV"
                      >
                        <Download size={11} /> CSV
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.15em] text-[var(--app-text-muted)] text-left border-b border-[var(--app-line)]">
                    <tr>
                      <th className="py-2 px-4">Address</th>
                      <th className="py-2 px-4">Sold Price</th>
                      <th className="py-2 px-4">Sold Date</th>
                      <th className="py-2 px-4">Sqft</th>
                      <th className="py-2 px-4">$/sqft</th>
                      <th className="py-2 px-4">Beds/Baths</th>
                      <th className="py-2 px-4">Year</th>
                      <th className="py-2 px-4">Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comps.comps.map((c, i) => {
                      const soldWhen = c.sold_date
                        ? new Date(c.sold_date).toLocaleDateString("en-US", {
                            year: "numeric", month: "short", day: "numeric",
                          })
                        : "—";
                      const ago = c.days_old ? `${c.days_old}d ago` : "";
                      const zillowHref = c.address
                        ? `https://www.zillow.com/homes/${encodeURIComponent(c.address)}_rb/`
                        : null;
                      return (
                        <tr
                          key={i}
                          className="border-b border-[var(--app-line)]/60 hover:bg-[var(--app-surface)] transition-colors"
                          data-testid={`comp-row-${i}`}
                        >
                          <td className="py-2 px-4">
                            {zillowHref ? (
                              <a
                                href={zillowHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[var(--app-accent-dark)] hover:text-[var(--app-accent)] hover:underline font-medium inline-flex items-center gap-1"
                                data-testid={`comp-link-${i}`}
                                title="Open in Zillow"
                              >
                                {c.address}
                                <ExternalLink size={11} strokeWidth={2.2} className="opacity-70" />
                              </a>
                            ) : (
                              <span>{c.address}</span>
                            )}
                            <div className="mt-0.5 flex gap-1">
                              {c.has_pool && (
                                <span className="text-[9px] uppercase tracking-[0.15em] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                  🏊 pool
                                </span>
                              )}
                              {c.is_waterfront && (
                                <span className="text-[9px] uppercase tracking-[0.15em] bg-cyan-50 text-cyan-700 px-2 py-0.5 rounded-full">
                                  🌊 waterfront
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-4 font-mono">{money(c.price)}</td>
                          <td className="py-2 px-4">
                            <div>{soldWhen}</div>
                            {ago && <div className="text-[10px] text-[var(--app-text-muted)]">{ago}</div>}
                          </td>
                          <td className="py-2 px-4 font-mono">{c.sqft}</td>
                          <td className="py-2 px-4 font-mono">${c.ppsf}</td>
                          <td className="py-2 px-4">
                            {c.bedrooms}/{c.bathrooms}
                          </td>
                          <td className="py-2 px-4">{c.year_built || "—"}</td>
                          <td className="py-2 px-4">
                            {c.distance_mi ? `${c.distance_mi.toFixed(2)} mi` : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Market stats — ZIP-level trends (RentCast /markets) */}
          {(marketBusy || market) && (
            <div className="card-flat p-5 mt-6 reveal reveal-2" data-testid="market-stats-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Market stats
                  </div>
                  <div className="font-display text-xl font-bold">
                    ZIP {market?.zipCode || property?.zip || "…"}
                    <span className="text-muted-foreground text-sm font-normal ml-2">
                      · 12-month sale + rental trend
                    </span>
                  </div>
                </div>
                {marketBusy && (
                  <Loader2 size={14} className="animate-spin text-muted-foreground" />
                )}
              </div>

              {/* Snapshot row */}
              {market && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <MarketStat label="Median sale" value={market.sale?.median_price} isMoney />
                  <MarketStat label="Median $/sqft" value={market.sale?.median_price_sqft} isMoney />
                  <MarketStat label="Sale DOM" value={market.sale?.median_dom} suffix=" days" />
                  <MarketStat label="Active listings" value={market.sale?.total_listings} />
                  <MarketStat label="Median rent" value={market.rent?.median_price} isMoney />
                  <MarketStat label="Rent DOM" value={market.rent?.median_dom} suffix=" days" />
                  <MarketStat label="Rentals active" value={market.rent?.total_listings} />
                  <MarketStat label="New listings" value={market.sale?.new_listings} />
                </div>
              )}

              {/* Historical trend — median sale price + DOM sparkline */}
              {market?.sale_history?.length > 0 && (
                <div className="mt-5" data-testid="market-history">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                    Sale price trend — {market.sale_history.length} months
                  </div>
                  <MarketSparkline
                    history={market.sale_history}
                    field="median_price"
                    format={(v) => money(v)}
                    stroke="#0ea5e9"
                  />
                  <div className="mt-3 text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                    Days-on-market trend
                  </div>
                  <MarketSparkline
                    history={market.sale_history}
                    field="median_dom"
                    format={(v) => `${v}d`}
                    stroke="#f59e0b"
                  />
                </div>
              )}
            </div>
          )}

          {/* Smart 3-Tier Offer Recommender — auto-fills from this property */}
          <div className="mt-6 reveal reveal-2">
            <SmartOfferRecommender
              propertyId={savedId || property.id}
              onOfferCreated={() => {
                toast.success("Offer saved — visit Offers to send for signature");
              }}
              onTiersComputed={(tiers) => {
                setSmartTiers(tiers);
                // Default to the "recommended" tier — user can still switch
                // between chips or type a custom amount below.
                const rec = tiers.find((t) => t.key === "recommended") || tiers[1] || tiers[0];
                if (rec) {
                  setPickedTierKey(rec.key);
                  setPickedOfferAmount(String(rec.price));
                }
              }}
            />
          </div>

          {/* AI Analyzer + Repair + Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <div className="card-flat p-5 reveal" data-testid="ai-analyzer">
              <div className="flex items-center gap-2">
                <Sparkles size={16} />
                <div className="font-display text-lg font-bold">AI Deal Analyzer</div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                GPT-5.2 reviews the full deal and recommends buy / hold / pass.
              </p>
              <Button
                onClick={runAiAnalysis}
                disabled={aiBusy}
                className="w-full mt-4 rounded-full h-11 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
                data-testid="run-ai-analysis"
              >
                {aiBusy ? (
                  <><Loader2 className="animate-spin mr-2" size={14} />Analyzing…</>
                ) : (
                  "Run Comp Analysis"
                )}
              </Button>
              {aiAnalysis && (
                <div className="mt-5 space-y-3" data-testid="ai-analysis-result">
                  <div className="flex items-center justify-between">
                    <Badge
                      className={`rounded-full ${
                        aiAnalysis.recommendation === "STRONG_BUY" ||
                        aiAnalysis.recommendation === "BUY"
                          ? "bg-[hsl(var(--success))] text-white"
                          : aiAnalysis.recommendation === "PASS"
                            ? "bg-destructive text-white"
                            : "bg-secondary text-foreground"
                      }`}
                    >
                      {aiAnalysis.recommendation?.replace("_", " ")}
                    </Badge>
                    <div className="text-right">
                      <div className="kpi-num text-3xl">
                        {aiAnalysis.confidence}%
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Confidence
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Recommended Offer
                    </div>
                    <div className="kpi-num text-2xl">
                      {money(aiAnalysis.recommended_offer)}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {aiAnalysis.reasoning}
                  </p>
                  {aiAnalysis.risks?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                        Risks
                      </div>
                      <ul className="text-xs space-y-1">
                        {aiAnalysis.risks.map((r, i) => (
                          <li key={i} className="flex gap-2">
                            <span className="text-destructive">•</span>{r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* AI Repair Estimator removed 2026-02-17 by owner. Component
                logic still lives in this file (fileInput ref, repairBusy,
                repairEst, onFiles) so it can be re-enabled by dropping
                the card JSX back here. */}

            <div className="card-flat p-5 reveal reveal-3" data-testid="actions-card">
              <div className="flex items-center gap-2">
                <FileText size={16} />
                <div className="font-display text-lg font-bold">Actions</div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Generate offer PDF, skip trace, or add to CRM.
              </p>

              <div className="mt-4 space-y-2">
                <Button
                  onClick={generateOffer}
                  className="w-full rounded-full h-11 bg-[var(--app-accent)] text-white hover:bg-[var(--app-accent-dark)]"
                  data-testid="generate-offer-button"
                >
                  <FileText size={14} className="mr-2" /> Generate cash offer PDF
                </Button>
                <button
                  onClick={() => setShowOverrides(true)}
                  data-testid="customize-terms-button"
                  className="w-full text-xs uppercase tracking-[0.18em] text-[var(--app-text-muted)] hover:text-[var(--app-text)] py-1.5"
                >
                  Customize contract terms for this deal
                </button>

                {/* Offer amount picker — the 3 Smart-Offer tiers as chips
                    + a fully custom dollar input. Whatever ends up in
                    `pickedOfferAmount` is what gets attached to the lead. */}
                {property && (
                  <div className="rounded-xl border border-[var(--app-line)] bg-[var(--app-surface)] p-3"
                       data-testid="pl-offer-picker">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                      Offer amount for this lead
                    </div>
                    {smartTiers.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {smartTiers.map((t) => {
                          const active = pickedTierKey === t.key;
                          return (
                            <button
                              key={t.key}
                              type="button"
                              onClick={() => {
                                setPickedTierKey(t.key);
                                setPickedOfferAmount(String(t.price));
                              }}
                              data-testid={`pl-offer-tier-${t.key}`}
                              className={`text-[11px] rounded-full px-3 py-1 border transition-colors ${
                                active
                                  ? "bg-[var(--app-black)] text-white border-transparent"
                                  : "bg-white text-[var(--app-text)] border-[var(--app-line)] hover:bg-[var(--app-surface-2)]"
                              }`}
                              title={`${t.arv_pct ? `${Math.round(t.arv_pct * 100)}% of ARV` : ""}`}
                            >
                              <span className="uppercase tracking-widest opacity-70 mr-1">
                                {t.key === "quick" ? "Quick" :
                                 t.key === "recommended" ? "Rec." :
                                 t.key === "max_safe" ? "Max" : t.key}
                              </span>
                              ${Number(t.price).toLocaleString()}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Custom&nbsp;$</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        placeholder={property.mao ? String(Math.round(property.mao)) : "185000"}
                        value={pickedOfferAmount}
                        onChange={(e) => {
                          setPickedOfferAmount(e.target.value);
                          setPickedTierKey(null);  // typing overrides the tier chip
                        }}
                        className="field-sharp h-9 font-mono text-sm"
                        data-testid="pl-offer-custom-input"
                      />
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1.5">
                      {pickedTierKey
                        ? `Using the ${pickedTierKey.replace("_", " ")} tier.`
                        : pickedOfferAmount
                          ? "Custom amount will be saved on the lead."
                          : "Leave blank to save without an offer amount."}
                    </div>
                  </div>
                )}

                {/* Add-to-Lead + Send-to-Pipeline */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => addToLead()}
                    disabled={addLeadBusy}
                    variant="secondary"
                    className="rounded-full h-11 justify-center bg-[var(--app-surface)] border border-[var(--app-line)]"
                    data-testid="pl-add-to-lead"
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      {addLeadBusy
                        ? <Loader2 size={14} className="animate-spin" />
                        : <UserPlus size={14} />}
                      Add to Lead
                    </span>
                  </Button>
                  <Button
                    onClick={() => addToLead({ openPipeline: true })}
                    disabled={addLeadBusy}
                    className="rounded-full h-11 px-4 justify-center bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)] whitespace-nowrap"
                    data-testid="pl-add-and-send"
                  >
                    <span className="inline-flex items-center justify-center gap-2">
                      <Layers size={14} />
                      Add & Send to Pipeline
                    </span>
                  </Button>
                </div>

                <Button
                  onClick={runSkipTrace}
                  variant="secondary"
                  disabled={skipBusy}
                  className="w-full rounded-full h-11 bg-[var(--app-surface)] text-[var(--app-text)] hover:bg-[var(--app-surface-2)] border border-[var(--app-line)]"
                  data-testid="skip-trace-button"
                >
                  {skipBusy ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : (
                    <PhoneCall size={14} className="mr-2" />
                  )}
                  Skip trace owner
                </Button>
                <Button
                  onClick={() => setShowEmail(true)}
                  disabled={!savedId}
                  className="w-full rounded-full h-11 bg-[var(--app-surface)] text-[var(--app-text)] hover:bg-[var(--app-surface-2)] border border-[var(--app-line)]"
                  data-testid="send-email-button"
                  title={
                    savedId
                      ? "Compose email to the owner"
                      : "Save the property first to send email"
                  }
                >
                  <Mail size={14} className="mr-2" />
                  Send email to owner
                </Button>
              </div>
              <EmailComposeDialog
                open={showEmail}
                onOpenChange={setShowEmail}
                propertyId={savedId}
                offerId={latestOfferId}
                defaultRecipientName={property?.owner_name || ""}
              />
              <SendToPipelineDialog
                open={sendPipelineOpen}
                onOpenChange={setSendPipelineOpen}
                leadId={createdLeadId}
              />

              {skip && (
                <div className="mt-4 text-xs space-y-2" data-testid="skip-result">
                  {(skip.phones?.length || skip.emails?.length || skip.relatives?.length) ? (
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-md px-2 py-1 inline-block">
                      ✓ From your cache · free
                    </div>
                  ) : (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                      <div className="text-xs font-medium">No skip data for this address yet.</div>
                      <div className="text-[11px] text-muted-foreground">
                        Look up the owner on{" "}
                        <a href={`https://www.truepeoplesearch.com/results?name=${encodeURIComponent(property?.owner_name || "")}&citystatezip=${encodeURIComponent(property?.address || "")}`}
                           target="_blank" rel="noreferrer"
                           className="text-[var(--app-accent)] hover:underline">
                          TruePeopleSearch
                        </a>
                        , then paste the results below — we&apos;ll cache them so future lookups are instant.
                      </div>
                      <button
                        onClick={() => setSkipPasteOpen(true)}
                        className="text-[10px] uppercase tracking-widest bg-[var(--app-black)] text-white rounded-full px-3 py-1.5 hover:bg-[var(--app-accent)]"
                        data-testid="skip-add-manual"
                      >
                        + Add manually
                      </button>
                    </div>
                  )}
                  <div>
                    <div className="uppercase tracking-widest text-[10px] text-muted-foreground">
                      Phones
                    </div>
                    <div className="space-y-1 mt-1">
                      {(skip.phones || []).length === 0 ? (
                        <div className="font-mono text-[var(--app-text-muted)]">—</div>
                      ) : (
                        skip.phones.map((p) => (
                          <div key={p} className="flex items-center gap-2">
                            <span className="font-mono flex-1">{p}</span>
                            <button
                              onClick={() => setCallSms({ phone: p, tab: "call" })}
                              className="text-[10px] uppercase tracking-widest border border-[var(--app-line)] rounded-full px-2 py-1 hover:bg-[var(--app-surface)]"
                              data-testid={`skip-call-${p}`}
                            >
                              <PhoneCall size={10} className="inline mr-1" />Call
                            </button>
                            <button
                              onClick={() => setCallSms({ phone: p, tab: "sms" })}
                              className="text-[10px] uppercase tracking-widest border border-[var(--app-line)] rounded-full px-2 py-1 hover:bg-[var(--app-surface)]"
                              data-testid={`skip-sms-${p}`}
                            >
                              <MessageSquare size={10} className="inline mr-1" />SMS
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-[10px] text-muted-foreground">
                      Emails
                    </div>
                    <div className="font-mono">{skip.emails.join(", ") || "—"}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-[10px] text-muted-foreground">
                      Relatives
                    </div>
                    <div>{skip.relatives.join(", ") || "—"}</div>
                  </div>
                  {(skip.phones?.length || skip.emails?.length) > 0 && (
                    <button
                      onClick={() => setSkipPasteOpen(true)}
                      className="text-[10px] uppercase tracking-widest text-[var(--app-accent)] hover:underline"
                      data-testid="skip-edit-manual"
                    >
                      + Add more numbers / emails
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <Dialog open={showOverrides} onOpenChange={setShowOverrides}>
        <DialogContent
          className="rounded-2xl max-w-lg"
          data-testid="overrides-dialog"
        >
          <DialogHeader>
            <DialogTitle>Customize Terms for This Offer</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">
            Blank fields fall back to your Buyer Profile defaults.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs">Earnest Money $</Label>
              <Input
                data-testid="override-earnest"
                className="field-sharp mt-1 font-mono"
                placeholder="1000"
                value={overrides.earnest_money}
                onChange={(e) =>
                  setOverrides({ ...overrides, earnest_money: e.target.value })
                }
              />
            </div>
            <div>
              <Label className="text-xs">Inspection Days</Label>
              <Input
                data-testid="override-inspection-days"
                className="field-sharp mt-1 font-mono"
                placeholder="15"
                value={overrides.override_inspection_days}
                onChange={(e) =>
                  setOverrides({
                    ...overrides,
                    override_inspection_days: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label className="text-xs">Closing Days</Label>
              <Input
                data-testid="override-closing-days"
                className="field-sharp mt-1 font-mono"
                placeholder="30"
                value={overrides.override_closing_days}
                onChange={(e) =>
                  setOverrides({
                    ...overrides,
                    override_closing_days: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <Label className="text-xs">Closing Costs Paid By</Label>
              <Select
                value={overrides.override_closing_costs_paid_by || undefined}
                onValueChange={(v) =>
                  setOverrides({
                    ...overrides,
                    override_closing_costs_paid_by: v,
                  })
                }
              >
                <SelectTrigger
                  data-testid="override-closing-paid-by"
                  className="field-sharp mt-1"
                >
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Buyer (100%)</SelectItem>
                  <SelectItem value="seller">Seller (100%)</SelectItem>
                  <SelectItem value="split">Split 50/50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">State (Governing Law)</Label>
              <Input
                data-testid="override-state"
                className="field-sharp mt-1 font-mono uppercase"
                maxLength={2}
                placeholder="FL"
                value={overrides.override_state}
                onChange={(e) =>
                  setOverrides({
                    ...overrides,
                    override_state: e.target.value.toUpperCase(),
                  })
                }
              />
            </div>
            <div>
              <Label className="text-xs">&quot;or Assigns&quot; on Buyer</Label>
              <Select
                value={
                  overrides.override_use_or_assigns === null
                    ? undefined
                    : overrides.override_use_or_assigns
                      ? "yes"
                      : "no"
                }
                onValueChange={(v) =>
                  setOverrides({
                    ...overrides,
                    override_use_or_assigns:
                      v === "yes" ? true : v === "no" ? false : null,
                  })
                }
              >
                <SelectTrigger
                  data-testid="override-or-assigns"
                  className="field-sharp mt-1"
                >
                  <SelectValue placeholder="Default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Include &quot;or Assigns&quot;</SelectItem>
                  <SelectItem value="no">Remove &quot;or Assigns&quot;</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={generateOfferWithOverrides}
              data-testid="generate-with-overrides-button"
              className="rounded-xl"
            >
              Generate PDF with these terms
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CallSmsDialog
        open={!!callSms}
        onOpenChange={(v) => !v && setCallSms(null)}
        phone={callSms?.phone || ""}
        name={property?.owner_name || ""}
        address={property?.address || address}
        defaultTab={callSms?.tab || "call"}
      />

      <SkipPasteDialog
        open={skipPasteOpen}
        onOpenChange={setSkipPasteOpen}
        address={property?.address || address}
        ownerName={property?.owner_name || ""}
        propertyId={savedId}
        onSaved={(data) => setSkip((prev) => ({ ...prev, ...data }))}
      />
    </div>
  );
}


/**
 * Editable Payoff Amount card removed — Smart Offer Recommender now
 * handles owner-price / payoff / cash-out end-to-end.
 */


// -------- Market panel helpers --------
// KPI cell for the market-stats row. Renders "—" when the value is null so
// the grid never has empty cells, and formats money when isMoney=true.
function MarketStat({ label, value, isMoney = false, suffix = "" }) {
  const fmt = (n) => {
    if (n == null || Number.isNaN(Number(n))) return "—";
    const num = Number(n);
    if (isMoney) {
      return num >= 1000
        ? `$${Math.round(num).toLocaleString()}`
        : `$${num.toFixed(2)}`;
    }
    return `${Math.round(num).toLocaleString()}${suffix}`;
  };
  return (
    <div className="rounded-xl border border-[var(--app-line)] bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className="font-mono font-semibold text-base mt-0.5">
        {fmt(value)}
      </div>
    </div>
  );
}

// Tiny inline SVG sparkline — no chart library needed. Draws a smooth line
// across the historical trend for whichever numeric field we pass in.
function MarketSparkline({ history = [], field, format = String, stroke = "#0ea5e9" }) {
  const points = history
    .map((h) => (h[field] != null ? Number(h[field]) : null))
    .filter((v) => v != null && !Number.isNaN(v));
  if (points.length < 2) {
    return <div className="text-xs text-muted-foreground italic">Not enough history</div>;
  }
  const W = 640;
  const H = 60;
  const PAD = 4;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(max - min, 1);
  const xStep = (W - PAD * 2) / (points.length - 1);
  const pathD = points.map((v, i) => {
    const x = PAD + i * xStep;
    const y = PAD + (H - PAD * 2) * (1 - (v - min) / range);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const first = points[0];
  const last = points[points.length - 1];
  const delta = ((last - first) / first) * 100;
  const deltaCls = delta > 0 ? "text-emerald-700" : delta < 0 ? "text-rose-700" : "text-slate-500";
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
        <path d={pathD} fill="none" stroke={stroke} strokeWidth="1.75" />
      </svg>
      <div className="flex items-center justify-between text-[11px] mt-1">
        <span className="text-muted-foreground">
          {history[0].month} · {format(first)}
        </span>
        <span className={`font-medium ${deltaCls}`}>
          {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
        </span>
        <span className="text-muted-foreground">
          {history[history.length - 1].month} · {format(last)}
        </span>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// PropGPT — natural-language Q&A on the currently-loaded property.
// Backed by REAPI /v2/PropGPT (requires OPENAI_API_KEY in backend .env).
// Renders as a compact chat panel below the Full Property Data grid so
// reps can ask things like "what's the debt-to-value?" or "any signals
// this owner might sell?" without leaving the property they're on.
// ─────────────────────────────────────────────────────────────────────────
function PropGptPanel({ property }) {
  const [q, setQ] = useState("");
  const [thread, setThread] = useState([]);
  const [busy, setBusy]     = useState(false);

  if (!property?.address) return null;

  const ask = async (question) => {
    if (!question.trim()) return;
    // Anchor every question to the property in view so PropGPT
    // understands the context ("this house" -> the specific address).
    const framed = `About the property at ${property.address}: ${question}`;
    setThread((t) => [...t, { role: "user", text: question }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/properties/propgpt",
        { question: framed });
      if (!data.ok) {
        setThread((t) => [...t, {
          role: "assistant",
          text: data.message || data.error || "PropGPT returned no answer.",
          error: true,
        }]);
        return;
      }
      const answer = typeof data.answer === "string"
        ? data.answer
        : JSON.stringify(data.answer, null, 2);
      setThread((t) => [...t, { role: "assistant", text: answer }]);
    } catch (e) {
      setThread((t) => [...t, {
        role: "assistant",
        text: e?.response?.data?.detail || e.message || "PropGPT request failed",
        error: true,
      }]);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "Summarise the seller's motivation in one sentence",
    "What's the estimated debt-to-value?",
    "Is the current owner likely to sell?",
    "How does the tax bill compare to similar homes?",
  ];

  return (
    <div className="card-flat mt-6" data-testid="propgpt-panel">
      <div className="p-5 border-b border-border flex items-center gap-2">
        <Sparkles size={16} className="text-emerald-600" />
        <div className="font-display text-lg font-bold">Ask PropGPT</div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Natural-language Q&amp;A · powered by REAPI
        </span>
      </div>

      <div className="p-5 space-y-3">
        {/* Thread */}
        {thread.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            Ask a question about <span className="font-mono">{property.address}</span>.
            Suggestions below — click any to run.
          </div>
        ) : (
          <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1"
               data-testid="propgpt-thread">
            {thread.map((m, i) => (
              <div key={i}
                   className={`text-sm rounded-xl p-3 ${
                     m.role === "user"
                       ? "bg-slate-100 text-slate-900"
                       : m.error
                       ? "bg-amber-50 text-amber-900 border border-amber-200"
                       : "bg-emerald-50 text-emerald-900 border border-emerald-200"
                   }`}
                   data-testid={`propgpt-msg-${i}`}
              >
                <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">
                  {m.role === "user" ? "You" : "PropGPT"}
                </div>
                <div className="whitespace-pre-wrap leading-relaxed">{m.text}</div>
              </div>
            ))}
            {busy && (
              <div className="text-sm rounded-xl p-3 bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" />
                <span className="text-xs text-emerald-800">Asking PropGPT…</span>
              </div>
            )}
          </div>
        )}

        {/* Suggested prompts */}
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              disabled={busy}
              onClick={() => ask(s)}
              className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              data-testid={`propgpt-suggestion-${s.slice(0, 10)}`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); ask(q); }}
          className="flex gap-2"
        >
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ask anything about this property…"
            disabled={busy}
            data-testid="propgpt-input"
            className="flex-1"
          />
          <Button
            type="submit"
            disabled={busy || !q.trim()}
            data-testid="propgpt-send"
            className="rounded-full"
          >
            <Send size={14} className="mr-1.5" /> Ask
          </Button>
        </form>
      </div>
    </div>
  );
}
